/**
 * Story detail panel - Column 2 of workspace
 * Story 199: View story details with PRD and design
 * Story 227: Edit PRDs, tech designs and tasks
 *
 * Displays story metadata with editable title, status badge,
 * and tabbed interface for PRD, Tech Design, and Tasks.
 * All tabs are now editable with autosave.
 */

import { useMemo, useEffect, useCallback, useState } from 'react';
import { useStory } from '../../hooks/useStory';
import { useStoryDocument } from '../../hooks/useStoryDocument';
import { useStoryTasks } from '../../hooks/useStoryTasks';
import { useDeletedSections } from '../../hooks/useSectionHistory';
import { useStoryDocuments } from '../../hooks/useStoryDocuments';
import type { McpStreamEvent, ConnectionStatus } from '../../hooks/useUpdatesStream';
import { StoryHeader } from './StoryHeader';
import { StoryMetadata } from './StoryMetadata';
import { StoryTabs, DOC_TAB_PREFIX, type TabId, type CustomTab } from './StoryTabs';
import { PRDEditor } from './PRDEditor';
import { DesignEditor } from './DesignEditor';
import { TasksEditor } from './TasksEditor';
import { UpdatesTab } from './UpdatesTab';
import { EmptyDocState } from './EmptyDocState';
import { EmptyTaskState } from './EmptyTaskState';
import { GenericDocContent } from './GenericDocContent';
import { DeletedSectionsModal } from '../editor/DeletedSectionsModal';
import './StoryDetailPanel.css';

/** Fixed doc type slugs that have dedicated editors (not generic) */
const FIXED_DOC_TYPES = new Set(['prd', 'design']);

interface StoryDetailPanelProps {
  projectSlug: string;
  storyId: string;
  /** Active tab (controlled from URL state) */
  activeTab?: TabId;
  /** Called when tab changes */
  onTabChange?: (tab: TabId) => void;
  /** Selected section key for scroll restoration */
  selectedSection?: string | null;
  /** Called when a section is clicked */
  onSectionSelect?: (sectionKey: string) => void;
  /** Called for cross-document navigation (pushes to browser history) */
  onNavigate?: (tab: TabId, sectionKey?: string) => void;
  /** Real-time events from Workspace (Story 253) */
  realtimeEvents?: McpStreamEvent[];
  /** WebSocket connection status */
  connectionStatus?: ConnectionStatus;
  /** Currently subscribed project ID */
  subscribedProjectId?: string | null;
  /** Connection error message */
  connectionError?: string | null;
  /** Callback to reconnect WebSocket */
  onReconnect?: () => void;
  /** Callback to clear events */
  onClearEvents?: () => void;
}

export function StoryDetailPanel({
  projectSlug,
  storyId,
  activeTab = 'prd',
  onTabChange,
  selectedSection,
  onSectionSelect,
  onNavigate,
  realtimeEvents = [],
  connectionStatus = 'disconnected',
  subscribedProjectId = null,
  connectionError = null,
  onReconnect,
  // onClearEvents not currently used - available for future "clear history" feature
}: StoryDetailPanelProps) {
  const { data: story, isLoading, error } = useStory(projectSlug, storyId);

  // Note: Cache invalidation is now handled at Workspace level (Story 253)
  // This component just receives events and passes them to UpdatesTab

  // Fetch document content based on active tab
  const {
    data: prdDoc,
    isLoading: prdLoading,
  } = useStoryDocument(projectSlug, storyId, 'prd');

  const {
    data: designDoc,
    isLoading: designLoading,
  } = useStoryDocument(projectSlug, storyId, 'design');

  const {
    data: tasksData,
    isLoading: tasksLoading,
  } = useStoryTasks(projectSlug, storyId);

  // Story 297: Discover all documents for dynamic tab rendering
  const { data: documentsData } = useStoryDocuments(projectSlug, storyId);
  const customTabs: CustomTab[] = useMemo(() => {
    if (!documentsData?.documents) return [];
    return documentsData.documents
      .filter((d) => !FIXED_DOC_TYPES.has(d.doc_type))
      .map((d) => ({
        docType: d.doc_type,
        label: d.display_name ?? d.doc_type,
      }));
  }, [documentsData]);

  // Story 232: Fetch deleted sections for both document types
  const { data: prdDeletedData } = useDeletedSections(projectSlug, storyId, 'prd');
  const { data: designDeletedData } = useDeletedSections(projectSlug, storyId, 'design');
  const [deletedModalDocType, setDeletedModalDocType] = useState<string | null>(null);

  // Get deleted count for current document tab
  const deletedCount = activeTab === 'prd'
    ? (prdDeletedData?.deleted_sections?.length ?? 0)
    : activeTab === 'design'
      ? (designDeletedData?.deleted_sections?.length ?? 0)
      : 0;

  // Handler for opening deleted sections modal from tab bar
  const handleViewDeleted = useCallback(() => {
    if (activeTab === 'prd' || activeTab === 'design') {
      setDeletedModalDocType(activeTab);
    }
  }, [activeTab]);

  // Extract PRD anchors for design section coverage dropdown
  // Story 293: Only include requirement-type sections (not informational/test_strategy)
  const prdAnchors = useMemo(() => {
    if (!prdDoc?.sections) return [];
    return prdDoc.sections
      .filter((s) => {
        // Only include sections that are requirements
        // If section_type is not set, infer from anchor presence (backward compat)
        const effectiveType = s.section_type ?? (s.anchor ? 'requirement' : 'informational');
        return effectiveType === 'requirement';
      })
      .map((s) => s.anchor)
      .filter((a): a is string => a !== null && a !== undefined);
  }, [prdDoc]);

  // Map PRD anchors to section titles for display in coverage dropdown
  // Story 293: Only include requirement-type sections
  const prdTitleMap = useMemo(() => {
    if (!prdDoc?.sections) return {};
    const map: Record<string, string> = {};
    for (const section of prdDoc.sections) {
      // Only include requirement-type sections
      const effectiveType = section.section_type ?? (section.anchor ? 'requirement' : 'informational');
      if (section.anchor && effectiveType === 'requirement') {
        map[section.anchor] = section.title;
      }
    }
    return map;
  }, [prdDoc]);

  // Extract design anchors for task anchor checklist
  const designAnchors = useMemo(() => {
    if (!designDoc?.sections) return [];
    return designDoc.sections
      .map((s) => s.anchor)
      .filter((a): a is string => a !== null && a !== undefined);
  }, [designDoc]);

  // Map design anchors to section titles for display in task coverage dropdown
  const designTitleMap = useMemo(() => {
    if (!designDoc?.sections) return {};
    const map: Record<string, string> = {};
    for (const section of designDoc.sections) {
      if (section.anchor) {
        map[section.anchor] = section.title;
      }
    }
    return map;
  }, [designDoc]);

  // Compute PRD metadata: which design sections cover each PRD anchor
  // This is derived from design sections' `covers` arrays (reverse lookup)
  const prdMetadata = useMemo(() => {
    if (!prdDoc?.sections || !designDoc?.sections) return {};

    const metadata: Record<string, { coveredBy: Array<{ anchor: string; title: string }> }> = {};

    // For each PRD section with an anchor, find design sections that cover it
    for (const prdSection of prdDoc.sections) {
      if (!prdSection.anchor) continue;

      const coveredBy: Array<{ anchor: string; title: string }> = [];

      for (const designSection of designDoc.sections) {
        if (designSection.covers?.includes(prdSection.anchor)) {
          coveredBy.push({
            anchor: designSection.anchor ?? designSection.section_key,
            title: designSection.title,
          });
        }
      }

      metadata[prdSection.anchor] = { coveredBy };
    }

    return metadata;
  }, [prdDoc?.sections, designDoc?.sections]);

  // Compute Design metadata: which tasks implement each design capability
  // This is derived from tasks' `implements` arrays (reverse lookup)
  const designMetadata = useMemo(() => {
    if (!designDoc?.sections || !tasksData?.tasks) return {};

    const metadata: Record<string, { taskCount: number; tasks: Array<{ seq: number; title: string; status: string }>; requiredTests: string[] }> = {};

    // For each design section with an anchor, find tasks that implement it
    for (const designSection of designDoc.sections) {
      if (!designSection.anchor) continue;

      const implementingTasks: Array<{ seq: number; title: string; status: string }> = [];

      for (const task of tasksData.tasks) {
        if (task.implements?.includes(designSection.anchor)) {
          implementingTasks.push({
            seq: task.seq,
            title: task.title,
            status: task.status,
          });
        }
      }

      metadata[designSection.anchor] = {
        taskCount: implementingTasks.length,
        tasks: implementingTasks,
        requiredTests: [], // TODO: compute from design section if needed
      };
    }

    return metadata;
  }, [designDoc?.sections, tasksData?.tasks]);

  // Scroll to selected section on mount or when section/tab changes
  // Handles both document sections (section-X) and tasks (task-X)
  // Retries to handle tab switch timing where component may not be mounted yet
  useEffect(() => {
    if (selectedSection) {
      let attempts = 0;
      const maxAttempts = 5;
      const retryDelay = 100;

      const tryScrollToElement = () => {
        let element: Element | null = null;

        if (selectedSection.startsWith('task-')) {
          // Task navigation: extract seq from "task-X" and find task row
          const taskSeq = selectedSection.slice(5);
          element = document.querySelector(`[data-testid="task-row-${taskSeq}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Also expand the task
            (element as HTMLElement).click();
            return true;
          }
        } else {
          // Document section navigation
          element = document.querySelector(`[data-testid="section-${selectedSection}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
          }
        }
        return false;
      };

      const attemptScroll = () => {
        attempts++;
        if (!tryScrollToElement() && attempts < maxAttempts) {
          // Element not found, retry after delay (tab content may still be mounting)
          setTimeout(attemptScroll, retryDelay);
        }
      };

      // Initial attempt after brief delay for render
      const timer = setTimeout(attemptScroll, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedSection, activeTab]);

  // Navigation callback: PRD -> Design (find section by anchor and navigate)
  // Uses onNavigate to push to browser history for back button support
  const handleNavigateToDesign = useCallback((anchor: string) => {
    // Match by anchor OR section_key (prdMetadata uses anchor ?? section_key)
    const targetSection = designDoc?.sections?.find(
      (s) => s.anchor === anchor || s.section_key === anchor
    );
    if (onNavigate) {
      // Push to history - scroll handled by selectedSection effect
      onNavigate('design', targetSection?.section_key);
    } else {
      // Fallback: just switch tab
      onTabChange?.('design');
    }
  }, [onNavigate, onTabChange, designDoc?.sections]);

  // Navigation callback: Design -> Task (switch to tasks tab)
  // Uses onNavigate to push to browser history for back button support
  const handleNavigateToTask = useCallback((taskSeq: string) => {
    if (onNavigate) {
      // Push to history with task seq as section key - scroll handled by selectedSection effect
      onNavigate('tasks', `task-${taskSeq}`);
    } else {
      // Fallback: just switch tab and manually scroll
      onTabChange?.('tasks');
      setTimeout(() => {
        const taskElement = document.querySelector(`[data-testid="task-row-${taskSeq}"]`);
        if (taskElement) {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (taskElement as HTMLElement).click();
        }
      }, 100);
    }
  }, [onNavigate, onTabChange]);

  // Navigation callback: Design -> PRD (find section by anchor and navigate)
  // Uses onNavigate to push to browser history for back button support
  const handleNavigateToPRD = useCallback((anchor: string) => {
    // Match by anchor OR section_key (for consistency with other lookups)
    const targetSection = prdDoc?.sections?.find(
      (s) => s.anchor === anchor || s.section_key === anchor
    );
    if (onNavigate) {
      // Push to history - scroll handled by selectedSection effect
      onNavigate('prd', targetSection?.section_key);
    } else {
      // Fallback: just switch tab
      onTabChange?.('prd');
    }
  }, [onNavigate, onTabChange, prdDoc?.sections]);

  if (isLoading) {
    return (
      <div className="story-detail-panel story-detail-panel--loading">
        Loading story...
      </div>
    );
  }

  if (error) {
    return (
      <div className="story-detail-panel story-detail-panel--error">
        Failed to load story: {error.message}
      </div>
    );
  }

  if (!story) {
    return (
      <div className="story-detail-panel story-detail-panel--empty">
        Story not found
      </div>
    );
  }

  return (
    <div className="story-detail-panel">
      <StoryHeader story={story} projectSlug={projectSlug} />
      <StoryMetadata story={story} />

      <StoryTabs
        activeTab={activeTab}
        onTabChange={onTabChange ?? (() => {})}
        storyId={storyId}
        customTabs={customTabs}
        deletedCount={deletedCount}
        onViewDeleted={handleViewDeleted}
      />
      <div className="story-detail-panel__content" data-testid={`${activeTab}-content`}>
        {activeTab === 'prd' && (
          <div className="story-detail-panel__tab-content">
            {prdLoading ? (
              <p className="story-detail-panel__loading">Loading PRD...</p>
            ) : prdDoc?.sections?.length ? (
              <PRDEditor
                key={storyId}
                projectSlug={projectSlug}
                storyId={storyId}
                sections={prdDoc.sections}
                metadata={prdMetadata}
                onSectionSelect={onSectionSelect}
                onNavigateToDesign={handleNavigateToDesign}
              />
            ) : (
              <EmptyDocState projectSlug={projectSlug} storyId={storyId} docType="prd" />
            )}
          </div>
        )}
        {activeTab === 'design' && (
          <div className="story-detail-panel__tab-content">
            {designLoading ? (
              <p className="story-detail-panel__loading">Loading Design...</p>
            ) : designDoc?.sections?.length ? (
              <DesignEditor
                key={storyId}
                projectSlug={projectSlug}
                storyId={storyId}
                sections={designDoc.sections}
                prdAnchors={prdAnchors}
                prdTitleMap={prdTitleMap}
                metadata={designMetadata}
                onSectionSelect={onSectionSelect}
                onNavigateToTask={handleNavigateToTask}
                onNavigateToPRD={handleNavigateToPRD}
              />
            ) : (
              <EmptyDocState projectSlug={projectSlug} storyId={storyId} docType="design" />
            )}
          </div>
        )}
        {activeTab === 'tasks' && (
          <div className="story-detail-panel__tab-content">
            {tasksLoading ? (
              <p className="story-detail-panel__loading">Loading tasks...</p>
            ) : tasksData?.tasks?.length ? (
              <TasksEditor
                key={storyId}
                projectSlug={projectSlug}
                storyId={storyId}
                tasks={tasksData.tasks}
                designAnchors={designAnchors}
                designTitleMap={designTitleMap}
                onNavigateToDesign={handleNavigateToDesign}
              />
            ) : (
              <EmptyTaskState projectSlug={projectSlug} storyId={storyId} />
            )}
          </div>
        )}
        {/* Story 297: Generic document tabs */}
        {activeTab.startsWith(DOC_TAB_PREFIX) && (
          <div className="story-detail-panel__tab-content">
            <GenericDocContent
              projectSlug={projectSlug}
              storyId={storyId}
              docType={activeTab.slice(DOC_TAB_PREFIX.length)}
              onSectionSelect={onSectionSelect}
            />
          </div>
        )}
        {/* Updates tab: Story 253 - Events now come from Workspace level */}
        <div
          className="story-detail-panel__tab-content"
          style={{ display: activeTab === 'updates' ? 'block' : 'none' }}
        >
          <UpdatesTab
            projectSlug={projectSlug}
            storyId={storyId}
            onNavigate={onNavigate}
            realtimeEvents={realtimeEvents}
            connectionStatus={connectionStatus}
            subscribedProjectId={subscribedProjectId}
            connectionError={connectionError}
            onReconnect={onReconnect}
          />
        </div>
      </div>

      {/* Story 232: Deleted sections modal - controlled from tab bar */}
      {deletedModalDocType && (
        <DeletedSectionsModal
          projectSlug={projectSlug}
          storyId={storyId}
          docType={deletedModalDocType}
          onClose={() => setDeletedModalDocType(null)}
        />
      )}
    </div>
  );
}
